import { Segment, SegmentTableItemType } from 'domains/segments/types';
import { Folder } from 'models/types';
import useUnarchiveSegment from './mutations/useUnarchiveSegment';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from './queries/keys';

type Props = {
    activeItem: Segment | Folder;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useUnarchiveSegmentItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const queryClient = useQueryClient();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully unarchived.`);
        refetchTableData();
        resetActiveItem();

        // Invalidate segments and archived segments queries to refresh the lists
        queryClient.invalidateQueries(QueryKeys.SEGMENTS);
        queryClient.invalidateQueries(QueryKeys.ARCHIVED_SEGMENTS);
        queryClient.invalidateQueries(QueryKeys.SEGMENT_FOLDERS());
        // Also invalidate all segment queries with different parameters (folderId, type)
        queryClient.invalidateQueries(['segments']);
    };

    const { isLoading, mutate: unarchiveSegment, isSuccess } = useUnarchiveSegment({
        onError,
        onSuccess,
    });

    const getUnarchiveItemFunction = () => {
        if (!activeItem || activeItem.type === SegmentTableItemType.FOLDER) {
            return () => {};
        }

        const unarchiveData = {
            id: Number(activeItem.id),
        };

        return () => unarchiveSegment(unarchiveData);
    };

    return { getUnarchiveItemFunction, isLoading, isSuccess };
};

export default useUnarchiveSegmentItem;
